// ============================================================================
// angle-mark.ts — géométrie (pure) de la marque d'un angle dans une figure
// contrainte : arc de cercle (ou petit carré si l'angle est droit) et position
// de son étiquette. Coordonnées ÉCRAN (y vers le bas).
//
// L'angle marqué est TOUJOURS le saillant (≤ 180°) entre les demi-droites
// [VA) et [VB), quel que soit l'ordre de a et b : l'arc tourne dans le sens qui
// va de A vers B par le plus court chemin, et l'étiquette est posée sur la
// bissectrice. (Avant : arc toujours dans le même sens → dessiné du mauvais côté,
// bombé vers le sommet, quand B précédait A ; étiquette à la moyenne brute des
// deux directions → de l'autre côté du sommet quand l'angle contenait la
// direction « gauche ».)
// ============================================================================

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface AngleMark {
  /** Angle droit (à 0,12 rad près) : marque en petit carré. */
  isRight: boolean;
  /** Chemin SVG de la marque (arc ou carré). */
  path: string;
  /** Position de l'étiquette (sur la bissectrice, à `radius + labelGap`). */
  label: ScreenPoint;
}

const RIGHT_TOLERANCE = 0.12;

/** Écart signé de a1 vers a2, ramené dans ]-π, π]. */
export function signedAngleDelta(a1: number, a2: number): number {
  let d = (a2 - a1) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

export function angleMark(
  V: ScreenPoint,
  A: ScreenPoint,
  B: ScreenPoint,
  opts: { radius?: number; square?: number; labelGap?: number } = {},
): AngleMark {
  const r = opts.radius ?? 18;
  const m = opts.square ?? 12;
  const gap = opts.labelGap ?? 12;
  const a1 = Math.atan2(A.y - V.y, A.x - V.x);
  const a2 = Math.atan2(B.y - V.y, B.x - V.x);
  const d = signedAngleDelta(a1, a2);
  const isRight = Math.abs(Math.abs(d) - Math.PI / 2) < RIGHT_TOLERANCE;
  // SVG : sweep-flag 1 = sens des angles croissants en coordonnées écran.
  const sweep = d >= 0 ? 1 : 0;
  const path = isRight
    ? `M ${V.x + m * Math.cos(a1)} ${V.y + m * Math.sin(a1)} ` +
      `L ${V.x + m * (Math.cos(a1) + Math.cos(a2))} ${V.y + m * (Math.sin(a1) + Math.sin(a2))} ` +
      `L ${V.x + m * Math.cos(a2)} ${V.y + m * Math.sin(a2)}`
    : `M ${V.x + r * Math.cos(a1)} ${V.y + r * Math.sin(a1)} ` +
      `A ${r} ${r} 0 0 ${sweep} ${V.x + r * Math.cos(a2)} ${V.y + r * Math.sin(a2)}`;
  const bis = a1 + d / 2;
  return {
    isRight,
    path,
    label: { x: V.x + (r + gap) * Math.cos(bis), y: V.y + (r + gap) * Math.sin(bis) },
  };
}
